import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Location } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { IoService } from '../../../services/io/io.service';
import { ReportService } from '../../../services/report/report.service';
import { AuthorizationService } from '../../../services/authorization/authorization.service';
import { DropdownOptionGroup, DropdownOption, DropdownSelectionMode } from '../../shared/interfaces/option-interfaces';
import { AuditComponent } from '../../shared/audit/audit.component';
import { DomSanitizer } from '@angular/platform-browser';
import { LocationService } from 'src/app/services/location/location.service'; 
import { ClientSettingsService } from 'src/app/services/client-settings/client-settings.service';

@Component({
  selector: 'app-detail-inventory',
  templateUrl: './detail-inventory.component.html',
  styleUrls: ['./detail-inventory.component.scss']
})
export class DetailInventoryComponent implements OnInit {

  documentId$: Observable<any>;
  documentId;

  activeLocation;

  account;
  accounts;
  showTaxControl = false;
  authorizations = {
    dependentItems: false,
    descendantItems: false,
    inventoryTransactions: false,
    manageVendors: false,
    archive: false,
  };

  useRollingAverageCost: boolean = false;
  image;

  @HostListener('window:message', ['$event']) onMessage(e) {
    //console.log('e.data: ', e.data);
  
    if (e.data && e.data.type == 'fileApiResponse') {
      console.log('UPLOADER RESPONSE PASSES!');
      this.image.url = e.data.url;
      this.image.name = e.data.name;
      this.imageUpdated();
    }
  }


  optionFields: Array<DropdownOptionGroup> = [
    {
      key: 'locationId',
      name: 'Available at Locations',
      options: new Array<DropdownOption>(),
      selection: new Array<DropdownOption>(),
      opposingIdKey: null,
      selectMode: DropdownSelectionMode.Multi,
    },
    {
      key: 'categoryId',
      name: 'Category',
      options: new Array<DropdownOption>(),
      selection: new Array<DropdownOption>(),
      opposingIdKey: null,
      selectMode: DropdownSelectionMode.One,
    },
    {
      key: 'subcategoryId',
      name: 'Subcategory',
      options: new Array<DropdownOption>(),
      selection: new Array<DropdownOption>(),
      opposingIdKey: null,
      selectMode: DropdownSelectionMode.One,
    },
    {
      key: 'groupId',
      name: 'Group(s)',
      options: new Array<DropdownOption>(),
      selection: new Array<DropdownOption>(),
      opposingIdKey: null,
      selectMode: DropdownSelectionMode.Multi,
    },
    {
      key: 'taxId',
      name: 'Taxes',
      options: new Array<DropdownOption>(),
      selection: new Array<DropdownOption>(),
      opposingIdKey: null,
      selectMode: DropdownSelectionMode.Multi,
    },
    {
      key: 'vendorId',
      name: 'Vendor(s)',
      options: new Array<DropdownOption>(),
      selection: new Array<DropdownOption>(),
      opposingIdKey: null,
      selectMode: DropdownSelectionMode.Multi
    }
  ];

  // vendorDropdown: DropdownOptionGroup = {
  //   key: 'vendorId',
  //   name: 'Vendor(s)',
  //   options: new Array<DropdownOption>(),
  //   selection: new Array<DropdownOption>(),
  //   opposingIdKey: null,
  //   selectMode: DropdownSelectionMode.Multi
  // };

  accountOptionField: DropdownOptionGroup = {
    key: 'options',
    name: 'Options',
    options: new Array<DropdownOption>(),
    selection: new Array<DropdownOption>(),
    opposingIdKey: null,
    selectMode: DropdownSelectionMode.None
  };

  permissions = {
    canViewAudit: false,
  };

  constructor(
    private locationR: Location,
    private route: ActivatedRoute,
    private router: Router,
    private ioService: IoService,
    private reportService: ReportService,
    private authorizationService: AuthorizationService,
    private clientSettingsService: ClientSettingsService,
    public sanitizer: DomSanitizer,
    private locationService: LocationService
  ) { }

  ngOnInit() {
    this.documentId$ = this.route.paramMap.pipe(switchMap(params => of(params.get('documentId'))));
    this.documentId$.subscribe(documentId => {
      if (documentId) {
        this.documentId = documentId;
        this.loadAccount();
      }
    });
    if ( this.authorizationService.checkPermission('Developer', 'general', true)) {
      this.permissions.canViewAudit = true;
    }

    this.setUseRollingAverageCost();
    // Subscribe to activeLocationSetObservable to get the active location
    this.locationService.activeLocationSetObservable().subscribe(location => {
      this.activeLocation = location;
    });
  }

  close() {
    this.reportService.closeDetailPanel(this.route);
  }

  back() {
    this.locationR.back();
  }

  unarchive = async () => {
    await this.ioService.post('/inventory/unarchive', {
      _id: this.account._id
    });
    
    this.loadAccount();
  }

  archive = async () => {
    await this.ioService.post('/inventory/archive', {
      _id: this.account._id
    });
    
    // this.loadAccount();

    setTimeout(() => {
      this.reportService.loadReport(null);
      this.close();
    }, 500);
  }

  loadAccount = async () => {
    console.log('loadInventoryDetail: ');
    this.account = null;

    let accountId = this.documentId;

    if (accountId.indexOf('-') > -1) {
      accountId = accountId.split('-')[0];
    }

    const account: any = await this.ioService.post('/inventory/getInventoryAccount', {
      accountId
    });

    for (const actId of Object.keys(account.accounts)) {
      if (!account.accounts[actId].calculatedCost && !account.accounts[actId].cost) {
        account.accounts[actId].cost = 0;
      }
    }

    this.accounts = account.accounts;

    // Define Required fields
    if (!account.taxId) {
      account.taxId = [];
    }

    if (!account.locationId) {
      account.locationId = [];
    }

    if (!Array.isArray(account.barcode)) {
      account.barcode = [account.barcode];
    }

    const barcodeObjects = [];
    for (const barcode of account.barcode) {
      barcodeObjects.push({
        value: barcode
      });
    }
    account.barcode = barcodeObjects;

    account.directCostAllowed = true;

    // Temporary Conversions
    account.price = (account.price / 100).toFixed(2);
    account.cost = (account.cost / 100).toFixed(4);
    if (account.options) {
      for (const option of account.options) {
        this.updateOptionGroupCosts(option, false);
        if (option.options) {
          for (const suboption of option.options) {
            if (suboption.price) {
              suboption.price = (suboption.price / 100).toFixed(2);
            }
          }
        }
      }
    }

    this.buildDropdown( 'locationId', account.authorizedLocations, account.locationId);
    this.buildDropdown( 'taxId', account.authorizedTaxes, account.taxId);
    this.buildDropdown( 'groupId', account.authorizedGroups, account.groupId);
    this.buildDropdown( 'categoryId', [account.category], account.categoryId);
    if (account.category?.subcategories) {
      this.buildDropdown( 'subcategoryId', account.category.subcategories, account.subcategoryId);
    }
    this.buildDropdown( 'vendorId', account.vendors, account.vendorId);

    account.views = [
      {
        name: 'Configuration',
        key: 'configuration'
      }
    ];
    if ( this.permissions.canViewAudit ) {
      account.views.push( {name: 'Audit', key: 'audit' } );
    }

    if (account.imgUrl) {
      account.imgPreview = "https://redfirefile.com/render?response=file&width=200&height=120&src="+account.imgUrl;
    }

    account.retailLocations = await this.locationService.getLocationsByType('retail');

    this.activeLocation = account.retailLocations[0];

    if (account.customFields) {
      for (const customField of account.customFields) {
        if (customField.locationSpecific && this.activeLocation) {
          if (!account[customField.key]) {
            account[customField.key] = {};
          }
          // account[customField.key][this.activeLocation._id] = null;
        }
      }
    }

    this.activeLocation = this.locationService.getActiveLocation();

    this.account = account;
    this.showTaxControl = (account.subtype !== 'selector' && account.subtype !== 'vendor');

    this.checkDirectCostAllowed();

    this.updateAccountCosts();

    if (this.authorizationService.permissionsAreLoaded()) {
      this.getAuthorizations();
    } else {
      this.authorizationService.permissionsLoadedObservable().subscribe(() => {
        this.getAuthorizations();
      });
    }

    if (this.authorizationService.checkPermission('uploadImages', 'any')) {
      this.initializeFileUploader();
    }

    // See if there are parent items
    account.hasParents = false;
    if (account.dependentAccountIds && account.dependentAccountIds.length > 0) {
      
      if (account.dependentAccountIds.length === 1 && account.dependentAccountIds[0]._id === account._id) {

      } else {
        account.hasParents = true;
      }
      
    }
    
  }

  initializeFileUploader = async () => {
    this.image = {};
    
    const uploader: any = await this.ioService.post('/file/requestUploader', {
      render: true,
      width: 300,
      // height: 1024,
    });
    let sanitizedIframeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(uploader.uploader.iFrameSrc);
    this.image.uploaderUrl = sanitizedIframeSrc;

  }

  imageUpdated = () => {
    this.image.preview = true;

    this.account.imgUrl = this.image.url;
    this.account.imgPreview = "https://redfirefile.com/render?width=200&height=120&src="+this.account.imgUrl;

  }




  getAuthorizations() {
    this.authorizations.dependentItems = this.authorizationService.checkPermission('report.'+this.reportService.getReportIdByKey('dependent-items'));
    this.authorizations.descendantItems = this.authorizationService.checkPermission('report.'+this.reportService.getReportIdByKey('descendant-items'));
    this.authorizations.inventoryTransactions = this.authorizationService.checkPermission('report.'+this.reportService.getReportIdByKey('inventory-transactions'));
    this.authorizations.manageVendors = this.authorizationService.checkPermission('Vendor.manage', 'general');
    this.authorizations.archive = this.authorizationService.checkPermission('archive');
    // console.log('this.authorizations: ', this.authorizations);
  }

  buildDropdown( key: string, rootOptions: any, rootSelections: any ) {
    const optionField = this.optionFields.find( (f) => f.key === key );
    if ( !optionField ) {
      return;
    }
    optionField.options = new Array<DropdownOption>();
    optionField.selection = new Array<DropdownOption>();
    for (const opt of rootOptions) {
      if ( !opt ) {
        continue;
      }
      const newOption = Object.assign(new DropdownOption( opt._id, opt._name ), opt );
      if (rootSelections && (Array.isArray(rootSelections) ? rootSelections.indexOf(opt._id) !== -1 : rootSelections === opt._id)) {
        optionField.selection.push( newOption );
      } else {
        optionField.options.push( newOption );
      }
    }
    rootOptions = null;
  }

  save = async (redirectAfter = true) => {
    this.account.errors = [];

    // Run validation
    if (!this.account.name) {
      this.account.errors.push('Name is required.');
    }

    if (!this.account.locationId || this.account.locationId.length === 0) {
      this.account.errors.push('You must select at least one location.');
    }

    // Create a clone of the account object
    const account = JSON.parse(JSON.stringify(this.account));

    this.updateDropdownKeys( account );

    // console.log('get vendor id');
    // try {
    //   account.vendorId = this.vendorDropdown.selection.map((s) => s._id);
    // } catch (e) {
    //   account.vendorId = null;
    // }

    // Remove Temporary Fields
    account.accounts = null;
    account.category = null;
    account.authorizedLocations = null;
    account.authorizedTaxes = null;
    account.authorizedGroups = null;
    account.views = null;
    account.activeOptionGroup = null;
    account.margin = null;
    account.marginPercent = null;

    let simpleBarcodes = [];
    if (account.barcode) {
      for (const barcode of account.barcode) {
        simpleBarcodes.push(barcode.value);
      }

      if (simpleBarcodes.length === 1) {
        simpleBarcodes = simpleBarcodes[0];
      }
      account.barcode = simpleBarcodes;
    }

    // Reverse Temporary Conversions
    if (!account.price || isNaN(account.price)) {
      account.price = null;
    } else {
      account.price = account.price * 100;
    }
    if (account.cost !== 0 && (!account.cost || isNaN(account.cost))) {
      account.cost = null;
    } else {
      account.cost = account.cost * 100;
    }

    if (!account.options || account.options.length == 0) {
      account.calculatedCost = null;
    }

    if (account.options) {
      for (const option of account.options) {
        if (option.options) {
          // if (option.type === 'components') {
          //   account.cost = 0;
          // }
          for (const suboption of option.options) {
            if (!suboption.price || isNaN(suboption.price) || account.subtype == 'selector') {
              suboption.price = null;
            } else {
              suboption.price = suboption.price * 100;
            }
          }
        }
      }
    }

    if (this.account.active && this.account.errors.length > 0) {
      return false;
    }

    // console.log('account to be saved:', account);

    await this.ioService.post('/account/updateAccount', {
      account
    });

    if (redirectAfter) {
      this.reportService.loadReport(null);
      if (this.account.subtype === 'component') {
        this.back();
      } else {
        this.close();
      }
    }

    return true;
  }

  updateDropdownKeys( account: any ) {
    for ( const field of this.optionFields ) {
      if ( field.selectMode === DropdownSelectionMode.One ) {
        account[field.key] = field.selection[0]?._id;
      } else {
        account[field.key] = field.selection.map((s) => s._id);
      }
    }
  }

  deleteValidate = async () => {
    if (confirm('Are you sure you want to delete this item?')) {
      this.deleteAccount();
    }
  }

  deleteAccount = async () => {
    try {
      const deleteResponse = await this.ioService.post('/inventory/deleteInventoryAccount', {
        accountId: this.account._id
      });

      setTimeout(() => {
        this.reportService.loadReport(null);
        this.close();
      }, 500);
    } catch (e) {
      // console.log('delete error: ', e.error);
      this.account.deleteError = e.error;
    }
  }

  viewDependentAccount = async (accountId) => {
    this.reportService.navigateWithinReport(['../', accountId], this.route);
  }

  viewVendorAccount = async (accountId) => {
    this.reportService.navigateWithinReport(['../../vendor-product/', accountId], this.route);
  }

  viewDiscountAccount = async (accountId) => {
    this.reportService.navigateWithinReport(['../../discount/', accountId], this.route);
  }

  cloneAccount = async () => {
    await this.save(false);

    // Temporary fix because account is being cloned before the save is complete
    await this.sleep(500);

    const newItem: any = await this.ioService.post('/account/cloneAccount', {
      accountId: this.account._id
    });

    this.reportService.loadReport(0);

    this.reportService.navigateWithinReport(['../', newItem._id], this.route);
  }

  createVendorProduct = async () => {
    console.log('createVendorProduct');
    await this.save(false);


    // Create a clone of the account object
    const account = JSON.parse(JSON.stringify(this.account));

    this.updateDropdownKeys( account );

    // Remove Temporary Fields
    account.accounts = null;
    account.category = null;
    account.authorizedLocations = null;
    account.authorizedTaxes = null;
    account.authorizedGroups = null;
    account.views = null;
    account.activeOptionGroup = null;
    account.margin = null;
    account.marginPercent = null;


// console.log('createVendorProduct: ', account);
    const newVendorProduct: any = await this.ioService.post('/inventory/addVendorProduct', {
      name: account.name,
      price: account.price * 100,
      cost: account.cost * 100,
      sku: account.sku,
      // barcode: account.barcode,
      unitOfMeasure: account.unitOfMeasure,
      unitDescription: account.unitDescription,
      remoteId: account.remoteId,
      categoryId: account.categoryId,
      retailProduct: {
        _id: account._id,
        name: account.name,
      }
    });

    this.viewVendorAccount(newVendorProduct._id);
  };

  subtypeChanged() {
    console.log('subtypeChanged: ');
    if (this.account.subtype === 'component') {
      this.account.viewOptions = false;
    }
  }

  addBarcode() {
    this.account.barcode.push({
      value: ''
    });
  }

  removeBarcode(barcodeIndex) {
    this.account.barcode.splice(barcodeIndex, 1);
  }

  optionGroupChanged(optionGroup: DropdownOptionGroup, searchText: string) {
    // console.log('optionGroupChanged: ', optionGroup);
    // console.log('searchText: ', searchText);

    if (optionGroup.key === 'categoryId' && this.account.subcategoriesActive) {
      // console.log('CATEGORY CHANGED');
      // console.log('subcategoryId: '+this.account.subcategoryId);
      this.account.subcategoryId = null;

      this.updateDropdownKeys(this.account);
      this.buildDropdown( 'subcategoryId', [], this.account.subcategoryId);
      for ( const field of this.optionFields ) {
        if (field.key === 'subcategoryId') {
          this.search( field, '' );
        }
      }
      
    }
  }

  async search( optionGroup: DropdownOptionGroup, searchText: string ) {
    let searchResults: Array<any>;
    const selection = optionGroup.selection.map( (s) => s._id );
    switch ( optionGroup.key ) {
      case 'locationId':
        searchResults = await this.searchLocations( selection, searchText );
        break;
      case 'groupId':
        searchResults = await this.searchGroups( selection, searchText );
        break;
      case 'categoryId':
        searchResults = await this.searchCategories( selection, searchText );
        break;
      case 'subcategoryId':
        searchResults = await this.searchSubcategories( selection, searchText );
        break;
      case 'taxId':
        searchResults = await this.searchTaxes( selection, searchText );
        break;
      case 'vendorId':
        searchResults = await this.searchVendors( selection, searchText );
        break;
    }
    optionGroup.options = searchResults.map((r) => Object.assign( new DropdownOption( r._id, r.name), r));
  }

  async searchVendors(currentSelection: any, searchText: string): Promise<Array<any>> {
    return await this.ioService.post('/location/getLocations', {
      active: true,
      type: 'vendor',
      search: searchText,
      limit: 10,
      omit: currentSelection
    }) as Array<any>;
  }

  async searchLocations(currentSelection: any, searchText: string): Promise<Array<any>> {
    return await this.ioService.post('/location/getLocations', {
      active: true,
      authorizationKey: 'account.inventory.manage',
      type: 'retail',
      search: searchText,
      limit: 10,
      omit: currentSelection
    }) as Array<any>;
  }

  async searchTaxes(currentSelection: any, searchText: string): Promise<Array<any>> {
    return await this.ioService.post('/account/getAccounts', {
      active: true,
      type: 'tax',
      locationId: this.account.locationId,
      search: searchText,
      limit: 10,
      omit: currentSelection
    }) as Array<any>;
  }

  async searchCategories(currentSelection: any, searchText: string): Promise<Array<any>> {
    return await this.ioService.post('/group/getGroups', {
      active: true,
      type: 'inventory',
      subtype: 'category',
      locationId: this.account.locationId,
      search: searchText,
      limit: 10,
      omit: currentSelection
    }) as Array<any>;
  }

  async searchSubcategories(currentSelection: any, searchText: string): Promise<Array<any>> {
    return await this.ioService.post('/group/getGroups', {
      active: true,
      type: 'inventory',
      subtype: 'category',
      parentId: this.account.categoryId,
      locationId: this.account.locationId,
      search: searchText,
      limit: 10,
      omit: currentSelection
    }) as Array<any>;
  }

  async searchGroups(currentSelection: any, searchText: string): Promise<Array<any>> {
    return await this.ioService.post('/group/getGroups', {
      active: true,
      type: 'inventory',
      subtype: 'group',
      locationId: this.account.locationId,
      search: searchText,
      limit: 10,
      omit: currentSelection
    }) as Array<any>;
  }

  convertArrayToObject = (array, key) => {
    const initialValue = {};
    return array.reduce((obj, item) => {
      return {
        ...obj,
        [item[key]]: item,
      };
    }, initialValue);
  }

  sleep = (milliseconds) => {
    return new Promise(resolve => setTimeout(resolve, milliseconds));
  }

  toggleBoolean(toggleTarget) {
    this.account[toggleTarget] = !this.account[toggleTarget];
  }

  toggleViewOptions() {
    this.account.viewOptions = !this.account.viewOptions;
  }

  toggleDetailView(viewKey) {
    if (this.account.activeView === viewKey) {
      this.account.activeView = null;
    } else {
      this.account.activeView = viewKey;
    }
  }

  setActiveOptionGroup(optionGroup) {
    this.account.activeOptionGroup = optionGroup;
  }

  addOptionGroup() {
    if (!this.account.options) {
      this.account.options = [];
    }

    const accountOption = {
      active: true,
      type: 'components',
      name: 'Components',
      key: Date.now().toString(),
      options: [],
    };

    switch (this.account.subtype) {
      case 'selector':
      case 'collection':
        accountOption.type = 'select';
        accountOption.name = 'Options';
        break;
    }

    this.updateOptionGroupCosts(accountOption);

    this.account.options.push(accountOption);

    this.setActiveOptionGroup(this.account.options[this.account.options.length - 1]);
  }

  removeOptionGroup(optionIndex) {
    if (this.account.activeOptionGroup && this.account.options[optionIndex] === this.account.activeOptionGroup) {
      this.setActiveOptionGroup(null);
    }
    this.account.options.splice(optionIndex, 1);

    this.updateAccountCosts();
  }

  async searchAccounts(searchText: string) {
    if (searchText && searchText.length > 2) {
      const accountParameters: any = {
        active: true,
        type: 'inventory',
        locationId: this.account.locationId,
        search: searchText,
        limit: 10,
        omit: this.account.activeOptionGroup.options.map( (s) => s.accountId ),
        returnCategoryName: true
      };

      if (this.account.subtype === 'selector') {
        accountParameters.subtype = 'retail';
      }

      accountParameters.omit.push(this.account._id);

      const accountResults = await this.ioService.post('/account/getAccounts', accountParameters) as Array<any>;
      this.accountOptionField.options = accountResults.map((r) => Object.assign( new DropdownOption(r._id, r.name), r));
    }
  }

  addNewAccount = async (name) => {
    const newAccountConfig = {
      type: 'inventory',
      locationId: this.account.locationId,
      subtype: 'component', // should be dynamic
      name,
      unitOfMeasure: 'EA'
    };

    switch (this.account.subtype) {
      case 'selector':
      case 'collection':
        newAccountConfig.subtype = 'product';
        break;
    }

    const account: any = await this.ioService.post('/account/addAccount', newAccountConfig);
    this.accounts[account._id] = account;
    this.addAccountOption(account);
  }

  addAccountOption( account ) {
    this.accounts[account._id] = account;

    if (account.price) {
      delete account.price;
    }

    this.account.activeOptionGroup.options.push( Object.assign( account, {
        active: true,
        accountId: account._id,
        name: account.shortName ?? account.name,
        qty: 1
      }));

    if (this.account.activeOptionGroup.type === 'components') {
      this.account.directCostAllowed = false;
    }

    this.checkDirectCostAllowed()
    this.updateOptionGroupCosts(this.account.activeOptionGroup);
  }

  updateAccountOptions = async () => {
    if ( this.accountOptionField.selection && this.accountOptionField.selection.length > 0 ) {
      this.addAccountOption( this.accountOptionField.selection[0]);
      this.accountOptionField.selection = new Array<DropdownOption>();
      this.updateAccountCosts();
    }
  }

  removeOption = async (optionIndex) => {
    this.account.activeOptionGroup.options.splice(optionIndex, 1);
    
    // See if there are any components
    this.checkDirectCostAllowed();
    this.updateOptionGroupCosts(this.account.activeOptionGroup);
  }

  checkDirectCostAllowed() {
    this.account.directCostAllowed = true;
    if (this.account.options && this.account.options.length > 0) {
      for (const option of this.account.options) {
        if (option.type === 'components' && option.options.length > 0) {
        // if (option.options.length > 0) {
          this.account.directCostAllowed = false;
          break;
        }
      }
    }

    console.log('this.account.directCostAllowed: ', this.account.directCostAllowed);

    if (this.account.directCostAllowed) {

      /*
      if (this.account.defaultVendorAccountId && !this.account.calculatedCost) {
        this.account.calculatedCost = {
          uncoveredHigh: this.account.cost*100,
          high: this.account.cost*100,
          low: this.account.cost*100,
          default: this.account.cost*100
        };
      }
      */
      
      
      if (this.account.calculatedCost && this.account.calculatedCost.high === this.account.calculatedCost.low) {
        this.account.cost = this.account.calculatedCost.high / 100;
        delete this.account.calculatedCost;
        console.log('costs high and low match');
        console.log('this.account.cost: ', this.account.cost);
      }
      
        
    }
    // console.log();
  }

  async setUseRollingAverageCost() {
    let setting: any = await this.clientSettingsService.getClientSetting("useRollingAverageCost");
    if(setting.success){
      this.useRollingAverageCost = setting.value; 
    }
  } 

  viewOption = async (accountId) => {
    console.log('viewOption: ', accountId);
    await this.save(false);
    console.log(['../', accountId]);
    this.reportService.navigateWithinReport(['../', accountId], this.route);
  }

  optionQtyUpdated(option) {
    console.log('optionQtyUpdated: ', option);
    switch (this.account.activeOptionGroup.type) {
      case 'select':
      case 'multiple':
        if (option.default) {
          option.default = option.qty;
        }
        break;
    }

    this.updateOptionGroupCosts(this.account.activeOptionGroup);
  }

  optionToggleDefault(option, multiplier = 1) {
    console.log('optionToggleDefault: ', option);
    switch (this.account.activeOptionGroup.type) {
      case 'select':
        if (option.default) {
          option.default = null;
        } else {
          for (const tempOption of this.account.activeOptionGroup.options) {
            tempOption.default = null;
          }

          option.default = multiplier; // option.qty;
        }
        break;

      case 'multiple':
        if (option.default) {
          option.default = null;
        } else {
          option.default = multiplier; // option.qty;
        }
        break;

      case 'multipleMultiple':
        if (option.default) {
          option.default = null;
        } else {
          option.default = multiplier;
        }
        break;
    }

    this.updateOptionGroupCosts(this.account.activeOptionGroup);
  }

  optionPriceUpdated() {
    this.updateOptionGroupCosts(this.account.activeOptionGroup);
  }

  updateOptionGroupCosts(optionGroup, doUpdateAccountCosts = true) {
    console.log('updateOptionGroupCosts: ', optionGroup);

    // if (!optionGroup.options || optionGroup.options.length === 0) {
      // return;
    // }

    // Update Costs
    let optionGroupCost = {
      uncoveredHigh: 0,
      high: 0,
      low: 0,
      default: 0
    };

    const optionCostStats = {
      uncoveredHigh: null,
      high: null,
      low: null,
      default: null
    };
console.log('optionGroup.type: ', optionGroup.type);


  if (optionGroup.options && optionGroup.options.length > 0) {
    switch (optionGroup.type) {
      case 'components':
        // All Components and included in the cost
        for (const suboption of optionGroup.options) {
          if (!this.accounts[suboption.accountId].calculatedCost) {
            this.accounts[suboption.accountId].calculatedCost = {
              uncoveredHigh: this.accounts[suboption.accountId].cost,
              high: this.accounts[suboption.accountId].cost,
              low: this.accounts[suboption.accountId].cost,
              default: this.accounts[suboption.accountId].cost
            };
          }

          optionGroupCost.uncoveredHigh += suboption.qty * this.accounts[suboption.accountId].calculatedCost.uncoveredHigh;
          optionGroupCost.high += suboption.qty * this.accounts[suboption.accountId].calculatedCost.high;
          optionGroupCost.low += suboption.qty * this.accounts[suboption.accountId].calculatedCost.low;
          optionGroupCost.default += suboption.qty * this.accounts[suboption.accountId].calculatedCost.default;
        }
        break;

      case 'select':
        // Only one of the components will be included in the cost

        for (const suboption of optionGroup.options) {
          if (!this.accounts[suboption.accountId].calculatedCost) {
            this.accounts[suboption.accountId].calculatedCost = {
              uncoveredHigh: this.accounts[suboption.accountId].cost,
              high: this.accounts[suboption.accountId].cost,
              low: this.accounts[suboption.accountId].cost,
              default: this.accounts[suboption.accountId].cost
            };
          }

          // Check for new uncoveredHigh
          if ((!optionCostStats.uncoveredHigh || suboption.qty * this.accounts[suboption.accountId].calculatedCost.uncoveredHigh > optionCostStats.uncoveredHigh) && !suboption.price) {
            optionCostStats.uncoveredHigh = suboption.qty * this.accounts[suboption.accountId].calculatedCost.uncoveredHigh;
          }

          // Check for new high
          if (!optionCostStats.high || suboption.qty * this.accounts[suboption.accountId].calculatedCost.high > optionCostStats.high) {
            optionCostStats.high = suboption.qty * this.accounts[suboption.accountId].calculatedCost.high;
          }

          // Check for new low
          if (!optionCostStats.low || suboption.qty * this.accounts[suboption.accountId].calculatedCost.low < optionCostStats.low) {
            optionCostStats.low = suboption.qty * this.accounts[suboption.accountId].calculatedCost.low;
          }

          // Check for new default
          if (!optionCostStats.default || suboption.default) {
            optionCostStats.default = suboption.default * suboption.qty * this.accounts[suboption.accountId].calculatedCost.default;
          }
        }
        optionGroupCost = optionCostStats;
        break;

      case 'multiple':
        // Multiple components may be included in the cost
        let minCount = 0;
        let maxCount = 9999;
        if (optionGroup.min) {
          minCount = optionGroup.min;
        }
        if (optionGroup.max) {
          maxCount = optionGroup.max;
        }


        const lowValues = [];
        const highValues = [];
        const uncoveredHighValues = [];

        if (optionGroup.options && optionGroup.options.length > 0) {
          for (const suboption of optionGroup.options) {
            if (!this.accounts[suboption.accountId].calculatedCost) {
              this.accounts[suboption.accountId].calculatedCost = {
                uncoveredHigh: this.accounts[suboption.accountId].cost,
                high: this.accounts[suboption.accountId].cost,
                low: this.accounts[suboption.accountId].cost,
                default: this.accounts[suboption.accountId].cost
              };
            }

            lowValues.push(suboption.qty * this.accounts[suboption.accountId].calculatedCost.low);
            highValues.push(suboption.qty * this.accounts[suboption.accountId].calculatedCost.high);
            if (!suboption.price) {
              uncoveredHighValues.push(suboption.qty * this.accounts[suboption.accountId].calculatedCost.uncoveredHigh);
            }

            if (suboption.default) {
              optionCostStats.default += suboption.default * suboption.qty * this.accounts[suboption.accountId].calculatedCost.default;
            }
          }

          // Get low by totaling the minCount cheapest options
          if (minCount == 0) {
            optionCostStats.low = 0;
          } else {
            lowValues.sort((a, b) => a - b);
            console.log('lowValues: ', lowValues);
            optionCostStats.low = lowValues.slice(0, minCount).reduce((a, b) => a + b);
          }

          // Get high by totaling the maxCount most expensive options
          highValues.sort((a, b) => b - a);
          console.log('highValues: ', highValues);
          optionCostStats.high = highValues.slice(0, maxCount).reduce((a, b) => a + b);

          // Get uncoveredHigh
          uncoveredHighValues.sort((a, b) => b - a);
          console.log('uncoveredHighValues: ', uncoveredHighValues);
          if (uncoveredHighValues && uncoveredHighValues.length > 0) {
            optionCostStats.uncoveredHigh = uncoveredHighValues.slice(0, maxCount).reduce((a, b) => a + b);
          } else {
            optionCostStats.uncoveredHigh = null;
          }

        }

        optionGroupCost = optionCostStats;
        break;

      case 'multipleMultiple':
        if (optionGroup.options && optionGroup.options.length > 0) {
          for (const suboption of optionGroup.options) {
            if (!this.accounts[suboption.accountId].calculatedCost) {
              this.accounts[suboption.accountId].calculatedCost = {
                uncoveredHigh: this.accounts[suboption.accountId].cost,
                high: this.accounts[suboption.accountId].cost,
                low: this.accounts[suboption.accountId].cost,
                default: this.accounts[suboption.accountId].cost
              };
            }

            let minCount = 0;
            let maxCount = 9999;
            if (optionGroup.min) {
              minCount = optionGroup.min;
            }
            if (optionGroup.max) {
              maxCount = optionGroup.max;
            }

            // Check for new uncoveredHigh
            if ((!optionCostStats.uncoveredHigh || suboption.qty * this.accounts[suboption.accountId].calculatedCost.uncoveredHigh * maxCount > optionCostStats.uncoveredHigh) && !suboption.price) {
              optionCostStats.uncoveredHigh = suboption.qty * this.accounts[suboption.accountId].calculatedCost.uncoveredHigh * maxCount;
            }

            // Check for new high
            if (!optionCostStats.high || suboption.qty * this.accounts[suboption.accountId].calculatedCost.high * maxCount > optionCostStats.high) {
              optionCostStats.high = suboption.qty * this.accounts[suboption.accountId].calculatedCost.high * maxCount;
            }

            // Check for new low
            if (!optionCostStats.low || suboption.qty * this.accounts[suboption.accountId].calculatedCost.low * minCount < optionCostStats.low) {
              optionCostStats.low = suboption.qty * this.accounts[suboption.accountId].calculatedCost.low * minCount;
            }

            // Check for new default
            if (!optionCostStats.default || suboption.default) {
              optionCostStats.default = suboption.default * suboption.qty * this.accounts[suboption.accountId].calculatedCost.default;
              // TODO: Add the cheapest options remaining to hit the minimum of the optionGroup

            }
          }
        }
        optionGroupCost = optionCostStats;
        break;
    }
  }

    optionGroup.calculatedCost = optionGroupCost;
    console.log('optionGroup.calculatedCost: ', optionGroup.calculatedCost);

    if (doUpdateAccountCosts) {
      this.updateAccountCosts();
    }
  }

  updateAccountCosts() {
    console.log('updateAccountCosts:');
    const accountCostStats = {
      uncoveredHigh: 0,
      high: 0,
      low: 0,
      default: 0
    };

    console.log('this.account.cost', this.account.cost);



    // if (this.account.options && this.account.options.length > 0 && !this.account.directCostAllowed) {
    if (this.account.options && this.account.options.length > 0) {

      // Allow direct cost on account in addition to cost of options

      // console.log('this.account in context of updaeAccountCosts: ', this.account);
      // console.log('directCostAllowed: '+this.account.directCostAllowed);
      

      if (this.account.cost && this.account.directCostAllowed) {
        accountCostStats.low += this.account.cost*100;
        accountCostStats.high += this.account.cost*100;
        accountCostStats.uncoveredHigh += this.account.cost*100;
        accountCostStats.default += this.account.cost*100;
        console.log('accountCostStats: ', accountCostStats);
      }

      for (const option of this.account.options) {
        console.log(option.calculatedCost);

        if (!option.calculatedCost) {
          option.calculatedCost = {
            low: 0,
            high: 0,
            uncoveredHigh: 0,
            default: 0
          };
        }

        accountCostStats.low += option.calculatedCost.low;
        accountCostStats.high += option.calculatedCost.high;
        accountCostStats.uncoveredHigh += option.calculatedCost.uncoveredHigh;
        accountCostStats.default += option.calculatedCost.default;
      }

      this.account.calculatedCost = accountCostStats;

      this.account.margin = (this.account.price - (this.account.calculatedCost.uncoveredHigh / 100))*100;
      console.log('account.calculatdCost: ', accountCostStats);
    } else {
      this.account.margin = (this.account.price - this.account.cost) * 100;

      // this.account.calculatedCost = null;
    }

    this.account.marginPercent = this.account.margin / this.account.price / 100;

    console.log('this.account.calculatedCost: ', this.account.calculatedCost);
  }

  // trackByIndex(index: number, value: number) {
  //   console.log("ngFor");
  //   console.log(index);
  //   console.log(value);
  //   return index;
  // }


  viewDependentItems() {
    console.log('viewDependentItems: ');
    this.reportService.loadFilteredReport('dependent-items', [
      {
        name: 'Items Containing '+this.account.name,
        type: 'link',
        value: this.account._id,
      },
      {
        name: 'Descendant Account Ids',
        path: 'descendantAccountIds',
        type: 'id',
        value: this.account._id,
        hidden: true,
      },
      {
        name: 'Exclude Self',
        path: '_id',
        type: 'idOmit',
        value: this.account._id,
        hidden: true,
      }
    ]);
  }

  viewDescendantItems() {
    console.log('viewDescendantItems: ');
    this.reportService.loadFilteredReport('descendant-items', [
      {
        name: 'Items Contained In '+this.account.name,
        type: 'link',
        value: this.account._id,
      },
      {
        name: 'Descendant Account Ids',
        path: 'accountId',
        type: 'id',
        value: this.account._id,
        hidden: true,
      }
    ]);
  }

  viewAudit() {
    AuditComponent.showModal( 'Account', this.documentId );
  }

  viewTransferHistory() {
    this.reportService.loadFilteredReport('inventory-transactions', [
      {
        name: 'Transfer History of '+this.account.name,
        type: 'link',
        value: this.account._id,
      },
      {
        name: 'Account ID',
        path: 'accountId',
        type: 'id',
        value: this.account._id,
        hidden: true,
      }
    ]);
  }
}

